import type { ComponentType } from 'react'

export type DebugRemoveBy = `${number}-${number}-${number}`

type DebugPanelSectionBase = {
  id: string
  title: string
  /** Lower sorts first. Defaults to 100. */
  order?: number
  Component: ComponentType
}

/** Cross-cutting QA tools with no expiry — use sparingly; see DEBUG_TOOLS.md. */
export type StableDebugPanelSectionRegistration = DebugPanelSectionBase & {
  stable: true
  removeBy?: never
}

/** Input for {@link registerStableDebugPanelSection} (no `removeBy` / `stable` fields). */
export type StableDebugPanelSectionInput = DebugPanelSectionBase

/** Feature-scoped tools that must be removed by `removeBy` (ESLint-enforced). */
export type TemporaryDebugPanelSectionRegistration = DebugPanelSectionBase & {
  stable?: false
  /** UTC calendar date `YYYY-MM-DD` — ESLint errors when missing or past due. */
  removeBy: DebugRemoveBy
}

export type DebugPanelSectionRegistration =
  | StableDebugPanelSectionRegistration
  | TemporaryDebugPanelSectionRegistration

export interface DebugChromeSlotRegistration {
  id: string
  /** UTC calendar date `YYYY-MM-DD` — ESLint errors when missing or past due. */
  removeBy: DebugRemoveBy
  order?: number
  useVisible: () => boolean
  Component: ComponentType
}

const panelSections = new Map<string, DebugPanelSectionRegistration>()
const chromeSlots = new Map<string, DebugChromeSlotRegistration>()

function assertUniqueId(kind: string, id: string, existing: Map<string, unknown>): void {
  if (existing.has(id)) {
    const message = `Duplicate debug ${kind} registration id: "${id}"`
    if (process.env.NODE_ENV !== 'production') {
      throw new Error(message)
    }
  }
}

function byOrder<T extends { order?: number; id: string }>(a: T, b: T): number {
  const orderDiff = (a.order ?? 100) - (b.order ?? 100)
  if (orderDiff !== 0) return orderDiff
  return a.id.localeCompare(b.id)
}

/** Register a section rendered inside the shared `?debug=true` popover. */
export function registerDebugPanelSection(def: DebugPanelSectionRegistration): void {
  assertUniqueId('panel section', def.id, panelSections)
  panelSections.set(def.id, def)
}

/** Register a permanent cross-cutting panel section (no `removeBy`). Prefer over `stable: true`. */
export function registerStableDebugPanelSection(def: StableDebugPanelSectionInput): void {
  registerDebugPanelSection({ ...def, stable: true })
}

/** Register a navbar-adjacent component with its own visibility hook. */
export function registerDebugChromeSlot(def: DebugChromeSlotRegistration): void {
  assertUniqueId('chrome slot', def.id, chromeSlots)
  chromeSlots.set(def.id, def)
}

export function getDebugPanelSections(): DebugPanelSectionRegistration[] {
  return Array.from(panelSections.values()).sort(byOrder)
}

export function getDebugChromeSlots(): DebugChromeSlotRegistration[] {
  return Array.from(chromeSlots.values()).sort(byOrder)
}

/** Clears registrations — for unit tests only. */
export function resetDebugRegistryForTests(): void {
  panelSections.clear()
  chromeSlots.clear()
}
